/**
 * SubProjectAgentPanel — Left pane wrapper for sub-project pages.
 *
 * Shows the Pilot orchestrator. When the Pilot fires an open-step event, the
 * panel navigates to the WorkspacePage for focused, field-level CFA editing.
 * (The legacy in-place CFA mode was superseded by the WorkspacePage in
 * commit 9212e61 and has been removed.)
 *
 * An always-visible "Agent specs" button (AgentInfoPanel) describes the Pilot's
 * capabilities; it is gated by showMetrics (SysAdmin / DevOps).
 */
import React, { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import PilotPanel from './PilotPanel';
import AgentInfoPanel from './AgentInfoPanel';
import { getActiveFieldForStep } from '../configs/stepRegistry';
import { getPilotCapabilities } from '../configs/agentCapabilities';
import type { StepOpenEvent } from '../hooks/usePilotChat';

interface SubProjectAgentPanelProps {
  subProjectId: string;
  subProjectName: string;
  kbId?: string;
  kbName?: string;
  solutionId?: string | null;
  language: 'en' | 'fr';
  showMetrics?: boolean;
  onActiveFieldChange: (field: string | null) => void;
  onSolutionIdChange?: (solutionId: string) => void;
  onOpenWorkspace?: (field: string, solutionId?: string) => void;
}

const SubProjectAgentPanel: React.FC<SubProjectAgentPanelProps> = ({
  subProjectId,
  subProjectName,
  kbId,
  kbName,
  solutionId,
  language,
  showMetrics,
  onActiveFieldChange,
  onSolutionIdChange,
  onOpenWorkspace,
}) => {
  const { t } = useTranslation();

  // Handle Pilot's open_step event — navigate to the field's WorkspacePage
  const handleStepOpen = useCallback((event: StepOpenEvent) => {
    const { stepId, solutionId: eventSolutionId } = event;

    if (eventSolutionId && onSolutionIdChange) {
      onSolutionIdChange(eventSolutionId);
    }

    const field = getActiveFieldForStep(stepId);
    if (field) {
      onActiveFieldChange(field);
      onOpenWorkspace?.(field, eventSolutionId ?? undefined);
    }
  }, [onActiveFieldChange, onSolutionIdChange, onOpenWorkspace]);

  return (
    <div className="flex flex-col h-full min-h-0">
      <PilotPanel
        subProjectId={subProjectId}
        subProjectName={subProjectName}
        kbId={kbId}
        kbName={kbName}
        solutionId={solutionId}
        language={language}
        onStepOpen={handleStepOpen}
        infoButton={
          <AgentInfoPanel
            capabilities={getPilotCapabilities(kbId, kbName)}
            agentLabelOverride={t('scv2.pilot.agentName', 'Pilot')}
            showMetrics={showMetrics}
          />
        }
      />
    </div>
  );
};

export default SubProjectAgentPanel;
